import type { BookingRuntimeResponse, BookingRouteAvailability } from '@ascleo/frontdesk-contracts/booking-questionnaire/runtime'
import { assign, setup } from 'xstate'

/**
 * Insurance types
 */
export type Insurance = 'GKV' | 'PKV' | 'SELFPAY'

/**
 * Patient data for scheduling
 */
export interface PatientData {
  id?: string
  firstName: string
  lastName: string
  dateOfBirth: string
  kvnr?: string
  insurance: Insurance
}

/**
 * Scheduler machine context
 */
export interface SchedulerContext {
  /** Transient questionnaire projection and server-derived service eligibility. */
  questionnaire?: BookingRuntimeResponse
  questionnaireLanguage?: string
  questionnaireAnswerCount?: number
  questionnaireEnabled?: boolean
  questionnaireCommand?: { kind: 'load' | 'back' } | { kind: 'answer'; answerId: string }
  questionnaireRoute?: BookingRouteAvailability
  /** Identified patient */
  patient?: PatientData
  /** Selected appointment type ID */
  appointmentTypeId?: string
  /** Patient-facing name of the selected type, captured from the type tile (KO-66) */
  appointmentTypeName?: string
  /** Selected slot ID */
  slotId?: string
  /** Calendar that owns the selected slot */
  slotCalendarId?: string
  /** Slot start time */
  slotStart?: string
  /** Slot end time */
  slotEnd?: string
  /** Optional patient-entered appointment note */
  appointmentDescription?: string
  /** Booking confirmation ID */
  bookingFailure?: 'pending' | 'rejected' | 'expired' | 'assistance'
  bookingId?: string
}

/**
 * Scheduler machine events
 */
export type SchedulerEvent =
  | { type: 'START_BOOKING' }
  | { type: 'RECOVERY_NONE' }
  | { type: 'QUESTIONNAIRE_ANSWER'; answerId: string }
  | { type: 'QUESTIONNAIRE_RESPONSE'; response: BookingRuntimeResponse; answerCount: number; language?: string }
  | { type: 'QUESTIONNAIRE_ROUTE'; route: BookingRouteAvailability }
  | { type: 'QUESTIONNAIRE_OFFER_CHANGED'; route: BookingRouteAvailability }
  | { type: 'QUESTIONNAIRE_ERROR' }
  | { type: 'QUESTIONNAIRE_RETRY' }
  | { type: 'QUESTIONNAIRE_ASSISTANCE' }
  | { type: 'QUESTIONNAIRE_LANGUAGE_CHANGED' }
  | { type: 'PATIENT_IDENTIFIED'; patient: PatientData }
  | { type: 'SELECT_APPOINTMENT_TYPE'; appointmentTypeId: string; appointmentTypeName?: string }
  | { type: 'SELECT_SLOT'; slotId: string; slotCalendarId: string; slotStart: string; slotEnd: string }
  | { type: 'SET_APPOINTMENT_DESCRIPTION'; description: string }
  | { type: 'SUBMIT_BOOKING' }
  // The confirmed times come from the server, not from what the kiosk had on
  // screen: a replayed booking may differ from the local selection, and the
  // patient must be shown the appointment that actually exists.
  | { type: 'BOOKING_SUCCESS'; bookingId: string; startTime?: string; endTime?: string }
  | { type: 'BOOKING_ERROR'; outcome?: 'pending' | 'rejected' | 'expired' | 'assistance' }
  | { type: 'BACK' }
  | { type: 'RESET' }

/**
 * Scheduler state identifiers
 */
export type SchedulerStateId =
  | 'recoveryLoading'
  | 'recoveryError'
  | 'questionnaireLoading'
  | 'questionnaire'
  | 'questionnaireError'
  | 'routeLoading'
  | 'patientIdentification'
  | 'appointmentType'
  | 'slotSelection'
  | 'confirmation'
  | 'booking'
  | 'done'
  | 'error'

/**
 * Scheduler XState machine
 * 
 * This machine orchestrates the appointment booking flow:
 * 1. Welcome screen
 * 2. Patient identification (card or manual)
 * 3. Select appointment type
 * 4. Select available time slot
 * 5. Confirm and complete booking
 */
export const schedulerMachine = setup({
  types: {
    context: {} as SchedulerContext,
    events: {} as SchedulerEvent
  },
  guards: {
    disabledQuestionnaire: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' && event.response.kind === 'disabled',
    bookingLeaf: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' && event.response.kind === 'booking',
    singlePrimary: ({ event }) => event.type === 'QUESTIONNAIRE_ROUTE' && event.route.kind === 'primary' && event.route.appointmentTypeIds.length === 1,
    routeReception: ({ event }) => (event.type === 'QUESTIONNAIRE_ROUTE' || event.type === 'QUESTIONNAIRE_OFFER_CHANGED') && event.route.kind === 'reception',
    hasEarlierAnswer: ({ context }) => (context.questionnaireAnswerCount ?? 0) > 0,
    enabledSinglePrimary: ({ context }) => (context.questionnaireAnswerCount ?? 0) > 0 && context.questionnaireEnabled === true && context.questionnaireRoute?.kind === 'primary' && context.questionnaireRoute.appointmentTypeIds.length === 1,
    enabledQuestionnaire: ({ context }) => context.questionnaireEnabled === true,
    permittedType: ({ context, event }) => event.type === 'SELECT_APPOINTMENT_TYPE' && (context.questionnaireEnabled !== true || (context.questionnaireRoute?.kind !== 'reception' && context.questionnaireRoute?.appointmentTypeIds.includes(event.appointmentTypeId) === true)),
    offeredAnswer: ({ context, event }) => event.type === 'QUESTIONNAIRE_ANSWER' && context.questionnaire?.kind === 'question' && context.questionnaire.answers.some((answer) => answer.answerId === event.answerId),
  },
  actions: {
    setBookingFailure: assign({ bookingFailure: ({ event }) => event.type === 'BOOKING_ERROR' ? event.outcome ?? 'pending' : undefined }),
    loadQuestionnaire: assign({ questionnaireCommand: () => ({ kind: 'load' as const }) }),
    backQuestionnaire: assign({ questionnaireCommand: () => ({ kind: 'back' as const }) }),
    answerQuestionnaire: assign({ questionnaireCommand: ({ event }) => event.type === 'QUESTIONNAIRE_ANSWER' ? { kind: 'answer' as const, answerId: event.answerId } : undefined }),
    setQuestionnaire: assign({
      questionnaireLanguage: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' ? event.language : undefined,
      questionnaire: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' ? event.response : undefined,
      questionnaireAnswerCount: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' ? event.answerCount : undefined,
      questionnaireEnabled: ({ event }) => event.type === 'QUESTIONNAIRE_RESPONSE' ? event.response.kind !== 'disabled' : undefined,
      questionnaireCommand: () => undefined,
    }),
    setQuestionnaireRoute: assign({
      questionnaireRoute: ({ event }) => (event.type === 'QUESTIONNAIRE_ROUTE' || event.type === 'QUESTIONNAIRE_OFFER_CHANGED') ? event.route : undefined,
      appointmentTypeId: ({ event }) => event.type === 'QUESTIONNAIRE_ROUTE' && event.route.kind === 'primary' && event.route.appointmentTypeIds.length === 1 ? event.route.appointmentTypeIds[0] : undefined,
      appointmentTypeName: () => undefined,
    }),
    clearSelection: assign({ appointmentTypeId: () => undefined, appointmentTypeName: () => undefined, slotId: () => undefined, slotCalendarId: () => undefined, slotStart: () => undefined, slotEnd: () => undefined, appointmentDescription: () => undefined, bookingId: () => undefined, bookingFailure: () => undefined, questionnaireRoute: () => undefined }),
    setPatient: assign({
      patient: ({ event }) =>
        event.type === 'PATIENT_IDENTIFIED' ? event.patient : undefined
    }),
    setAppointmentType: assign({
      appointmentTypeId: ({ event }) =>
        event.type === 'SELECT_APPOINTMENT_TYPE' ? event.appointmentTypeId : undefined,
      appointmentTypeName: ({ event }) =>
        event.type === 'SELECT_APPOINTMENT_TYPE' ? event.appointmentTypeName : undefined
    }),
    setSlot: assign({
      slotId: ({ event }) =>
        event.type === 'SELECT_SLOT' ? event.slotId : undefined,
      slotCalendarId: ({ event }) =>
        event.type === 'SELECT_SLOT' ? event.slotCalendarId : undefined,
      slotStart: ({ event }) =>
        event.type === 'SELECT_SLOT' ? event.slotStart : undefined,
      slotEnd: ({ event }) =>
        event.type === 'SELECT_SLOT' ? event.slotEnd : undefined
    }),
    setBookingId: assign({
      bookingFailure: () => undefined,
      bookingId: ({ event }) =>
        event.type === 'BOOKING_SUCCESS' ? event.bookingId : undefined,
      // A replayed booking returns the times of the appointment that exists,
      // which may differ from the slot last selected here. Confirming the
      // local selection instead would tell the patient about an appointment
      // they do not have.
      slotStart: ({ context, event }) =>
        event.type === 'BOOKING_SUCCESS' && event.startTime !== undefined
          ? event.startTime
          : context.slotStart,
      slotEnd: ({ context, event }) =>
        event.type === 'BOOKING_SUCCESS' && event.endTime !== undefined
          ? event.endTime
          : context.slotEnd
    }),
    setAppointmentDescription: assign({
      appointmentDescription: ({ event }) =>
        event.type === 'SET_APPOINTMENT_DESCRIPTION' ? event.description : undefined
    }),
    resetContext: assign({
      questionnaireLanguage: () => undefined,
      questionnaire: () => undefined,
      questionnaireAnswerCount: () => undefined,
      questionnaireEnabled: () => undefined,
      questionnaireCommand: () => undefined,
      questionnaireRoute: () => undefined,
      patient: () => undefined,
      appointmentTypeId: () => undefined,
      appointmentTypeName: () => undefined,
      slotId: () => undefined,
      slotCalendarId: () => undefined,
      slotStart: () => undefined,
      slotEnd: () => undefined,
      appointmentDescription: () => undefined,
      bookingId: () => undefined,
      bookingFailure: () => undefined
    })
  }
}).createMachine({
  id: 'scheduler',
  initial: 'patientIdentification',
  context: {},
  on: {
    RESET: { target: '.patientIdentification', actions: 'resetContext' },
  },
  states: {
    recoveryLoading: {
      on: {
        RECOVERY_NONE: { target: 'questionnaireLoading', actions: 'loadQuestionnaire' },
        BOOKING_SUCCESS: { target: 'done', actions: 'setBookingId' },
        BOOKING_ERROR: { target: 'recoveryError', actions: 'setBookingFailure' },
        BACK: { target: 'patientIdentification', actions: 'resetContext' },
        RESET: { target: 'patientIdentification', actions: 'resetContext' },
      },
    },
    recoveryError: {
      on: {
        BACK: { target: 'recoveryLoading' },
        RESET: { target: 'patientIdentification', actions: 'resetContext' },
      },
    },
    questionnaireLoading: {
      on: {
        QUESTIONNAIRE_RESPONSE: [
          { guard: 'disabledQuestionnaire', target: 'appointmentType', actions: 'setQuestionnaire' },
          { guard: 'bookingLeaf', target: 'routeLoading', actions: 'setQuestionnaire' },
          { target: 'questionnaire', actions: 'setQuestionnaire' },
        ],
        QUESTIONNAIRE_ERROR: { target: 'questionnaireError' },
        BACK: { target: 'patientIdentification', actions: 'resetContext' },
      },
    },
    questionnaire: {
      on: {
        QUESTIONNAIRE_ASSISTANCE: { guard: ({ context }) => context.questionnaire?.kind === 'unavailable', actions: ['clearSelection', assign({ questionnaire: (): BookingRuntimeResponse => ({ kind: 'reception', text: '' }) })] },
        QUESTIONNAIRE_LANGUAGE_CHANGED: { guard: 'enabledQuestionnaire', target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        QUESTIONNAIRE_ANSWER: { guard: 'offeredAnswer', target: 'questionnaireLoading', actions: ['clearSelection', 'answerQuestionnaire'] },
        QUESTIONNAIRE_RETRY: { target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        BACK: [
          { guard: 'hasEarlierAnswer', target: 'questionnaireLoading', actions: ['clearSelection', 'backQuestionnaire'] },
          { target: 'patientIdentification', actions: 'resetContext' },
        ],
      },
    },
    questionnaireError: {
      on: {
        QUESTIONNAIRE_LANGUAGE_CHANGED: { guard: 'enabledQuestionnaire', target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        QUESTIONNAIRE_RETRY: { target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        BACK: { target: 'patientIdentification', actions: 'resetContext' },
      },
    },
    routeLoading: {
      on: {
        QUESTIONNAIRE_ROUTE: [
          { guard: 'routeReception', target: 'questionnaire', actions: ['setQuestionnaireRoute', assign({ questionnaire: () => ({ kind: 'reception' as const, text: '' }) })] },
          { guard: 'singlePrimary', target: 'slotSelection', actions: 'setQuestionnaireRoute' },
          { target: 'appointmentType', actions: 'setQuestionnaireRoute' },
        ],
        QUESTIONNAIRE_ERROR: { target: 'questionnaireError' },
        BACK: { target: 'patientIdentification', actions: 'resetContext' },
      },
    },
    patientIdentification: {
      on: {
        PATIENT_IDENTIFIED: {
          target: 'recoveryLoading',
          actions: ['resetContext', 'setPatient']
        }
      }
    },
    appointmentType: {
      on: {
        QUESTIONNAIRE_LANGUAGE_CHANGED: { guard: 'enabledQuestionnaire', target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        BACK: [
          { guard: 'hasEarlierAnswer', target: 'questionnaireLoading', actions: ['clearSelection', 'backQuestionnaire'] },
          { target: 'patientIdentification', actions: 'resetContext' },
        ],
        SELECT_APPOINTMENT_TYPE: {
          guard: 'permittedType',
          target: 'slotSelection',
          actions: 'setAppointmentType'
        }
      }
    },
    slotSelection: {
      on: {
        QUESTIONNAIRE_OFFER_CHANGED: [
          { guard: 'routeReception', target: 'questionnaire', actions: ['clearSelection', 'setQuestionnaireRoute', assign({ questionnaire: () => ({ kind: 'reception' as const, text: '' }) })] },
          { target: 'appointmentType', actions: ['clearSelection', 'setQuestionnaireRoute'] },
        ],
        QUESTIONNAIRE_LANGUAGE_CHANGED: { guard: 'enabledQuestionnaire', target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        BACK: [
          { guard: 'enabledSinglePrimary', target: 'questionnaireLoading', actions: ['clearSelection', 'backQuestionnaire'] },
          { guard: ({ context }) => context.questionnaireEnabled === true && (context.questionnaireAnswerCount ?? 0) === 0 && context.questionnaireRoute?.kind === 'primary' && context.questionnaireRoute.appointmentTypeIds.length === 1, target: 'patientIdentification', actions: 'resetContext' },
          { target: 'appointmentType', actions: assign({ slotId: () => undefined, slotCalendarId: () => undefined, slotStart: () => undefined, slotEnd: () => undefined, appointmentDescription: () => undefined }) },
        ],
        SELECT_SLOT: {
          target: 'confirmation',
          actions: 'setSlot'
        }
      }
    },
    confirmation: {
      on: {
        BOOKING_ERROR: { target: 'error', actions: 'setBookingFailure' },
        QUESTIONNAIRE_LANGUAGE_CHANGED: { guard: 'enabledQuestionnaire', target: 'questionnaireLoading', actions: ['clearSelection', 'loadQuestionnaire'] },
        BACK: { target: 'slotSelection' },
        SET_APPOINTMENT_DESCRIPTION: {
          actions: 'setAppointmentDescription'
        },
        SUBMIT_BOOKING: { target: 'booking', actions: assign({ bookingFailure: () => undefined }) }
      }
    },
    // Submitting is its own state so the confirm control is inert from the
    // machine rather than from local component state: a second tap during the
    // request reaches no transition at all.
    booking: {
      on: {
        BOOKING_SUCCESS: {
          target: 'done',
          actions: 'setBookingId'
        },
        BOOKING_ERROR: { target: 'error', actions: 'setBookingFailure' }
      }
    },
    done: {
      on: {
        RESET: {
          target: 'patientIdentification',
          actions: 'resetContext'
        }
      }
    },
    error: {
      on: {
        BACK: { target: 'confirmation' },
        RESET: {
          target: 'patientIdentification',
          actions: 'resetContext'
        }
      }
    }
  }
})
